/**
 * Helpers for the IPO Dashboard (/ipo-dashboard).
 *
 * - IPOs: the GMP board (every issue from upcoming to listing) joined with the
 *   NSE schedules and subscription feed for mainboard; the SME list
 *   (GET /api/core/ipos/sme via utils/smeIpos.js buildSmeIpoRows) for SME
 * - Subscription: GET /api/market/subscription (+ /sme)
 * - Reviews: GET /api/core/ipos/{id}/broker-reviews for mainboard issues found
 *   in the catalog; GET /api/core/ipos/sme/reviews for SME
 * - Documents: the DRHP tracker (utils/drhpTracker.js buildDrhpRows)
 */
import { deriveBoard, parsePriceRange, toIsoDate } from './ipoCatalogAdapters';
import { formatDateRange, formatDayShort, todayIso } from './ipoCalendar';
import { formatShortDate } from './gmpBoard';
import { toIstStamp } from './ipoSubscription';
import { compareSubscriptionRows } from './subscriptionMapper';
import { DRHP_STATUS } from './drhpTracker';
import { PLATFORMS, isSameCompany } from './smeIpos';

export const DASHBOARD_BOARDS = {
  mainboard: { key: 'mainboard', label: 'Mainboard IPOs', shortLabel: 'Mainboard' },
  sme: { key: 'sme', label: 'SME IPOs', shortLabel: 'SME' },
};

// --- IPO calendar ------------------------------------------------------------------

const formatBand = (text) => {
  const { low, high } = parsePriceRange(text || '');
  if (!high) return null;
  return low && low !== high ? `₹${low} – ₹${high}` : `₹${high}`;
};

const phaseFor = (openOn, closeOn, today) => {
  if (openOn && today < openOn) return 'upcoming';
  if (closeOn && today > closeOn) return 'closed';
  if (closeOn && today === closeOn) return 'closing_today';
  return openOn ? 'open' : 'upcoming';
};

/**
 * Mainboard issues: the GMP board lists each issue from upcoming to listing;
 * NSE feeds add the symbol, the exact dates and the price band, and any issue
 * the grey market desk doesn't quote yet.
 *
 * @param {object} feeds
 * @param {object[]} feeds.gmpRows - GMP board rows (selectGmpBoardRows); SME rows are ignored
 * @param {object[]} feeds.subscriptionRows - selectSubscriptionRows
 * @param {object[]} feeds.liveItems - raw NSE items of GET /api/market/ipos/live (current + upcoming)
 * @param {object[]} feeds.scheduleRows - selectOpeningSoonIpos
 */
export const buildMainboardIpoRows = ({ gmpRows = [], subscriptionRows = [], liveItems = [], scheduleRows = [] }, today = todayIso()) => {
  const rows = gmpRows
    .filter((row) => row.board === 'Mainboard')
    .map((row) => ({
      company: row.name,
      symbol: '',
      status: row.status === 'other' ? null : row.status,
      openOn: row.openOn,
      closeOn: row.closeOn,
      listingOn: row.listingOn,
      priceBand: row.price ? `₹${row.price}` : null,
      exchange: row.exchange,
      gmp: row.hasGmp ? row.gmp : null,
      gmpSlug: row.slug,
      fromNse: false,
    }));

  const nseIssues = [
    ...subscriptionRows
      .filter((row) => row.board === 'mainboard')
      .map((row) => ({ company: row.company, symbol: row.symbol, openOn: row.openOn, closeOn: row.closeOn, priceBand: formatBand(row.priceBand) })),
    ...liveItems
      .filter((item) => item?.companyName && deriveBoard(item.series) === 'mainboard')
      .map((item) => ({
        company: item.companyName.trim(),
        symbol: item.symbol,
        openOn: toIsoDate(item.issueStartDate),
        closeOn: toIsoDate(item.issueEndDate),
        priceBand: formatBand(item.issuePrice || item.priceBand),
      })),
    ...scheduleRows
      .filter((row) => row.board === 'mainboard')
      .map((row) => ({ company: row.company, symbol: row.symbol, openOn: row.openOn, closeOn: row.closeOn, priceBand: row.priceBand })),
  ];

  nseIssues.forEach((issue) => {
    const row = rows.find((candidate) => isSameCompany(candidate.company, issue.company));
    if (!row) {
      rows.push({ ...issue, status: null, listingOn: null, exchange: 'NSE', gmp: null, gmpSlug: null, fromNse: true });
      return;
    }
    // NSE's dates and band are exact; the grey market desk's carry no year and only the upper price
    row.symbol = row.symbol || issue.symbol || '';
    if (issue.openOn) row.openOn = issue.openOn;
    if (issue.closeOn) row.closeOn = issue.closeOn;
    if (issue.priceBand) row.priceBand = issue.priceBand;
    row.fromNse = true;
  });

  return rows.map((row) => {
    let { status } = row;
    // NSE dates decide open/upcoming/closed; the grey market status adds allotted/listed
    if (row.fromNse || !status) {
      const phase = phaseFor(row.openOn, row.closeOn, today);
      status = phase === 'closed' && ['allotted', 'listed'].includes(status) ? status : phase;
    }
    return { ...row, status };
  });
};

// Dashboard statuses: 'open' | 'upcoming' | 'yet_to_list' | 'listed'
export const dashboardStatus = (row, today = todayIso()) => {
  if (row.status === 'open' || row.status === 'closing_today') return 'open';
  if (row.status === 'upcoming') return 'upcoming';
  if (row.status === 'listed' || (row.listingOn && row.listingOn <= today)) return 'listed';
  return 'yet_to_list';
};

const STATUS_ORDER = { open: 0, upcoming: 1, yet_to_list: 2, listed: 3 };

/**
 * Calendar rows for either board: mainboard rows from buildMainboardIpoRows,
 * SME rows from buildSmeIpoRows.
 */
export const toDashboardIpos = (rows, board, today = todayIso()) =>
  rows
    .map((row) => {
      const status = dashboardStatus(row, today);
      const exchange = board === 'sme' ? PLATFORMS[row.exchange] || 'BSE SME / NSE Emerge' : row.exchange || 'NSE';
      let link = null;
      if (row.symbol) link = `/ipo-subscription/${encodeURIComponent(row.symbol)}`;
      else if (row.gmpSlug) link = `/ipo-gmp/${row.gmpSlug}`;
      return {
        id: row.symbol || row.id || row.company,
        company: row.company,
        symbol: row.symbol || '',
        segment: [row.symbol ? `NSE: ${row.symbol}` : null, exchange, row.gmp !== null && row.gmp !== undefined ? `GMP ₹${row.gmp}` : null]
          .filter(Boolean)
          .join(' • '),
        openOn: row.openOn || null,
        dates: row.openOn && row.closeOn ? formatDateRange(row.openOn, row.closeOn) : 'To be announced',
        price: row.priceBand || 'To be announced',
        status,
        link,
      };
    })
    .sort(
      (a, b) =>
        STATUS_ORDER[a.status] - STATUS_ORDER[b.status] ||
        (a.status === 'upcoming' ? (a.openOn || '9999').localeCompare(b.openOn || '9999') : (b.openOn || '').localeCompare(a.openOn || '')) ||
        a.company.localeCompare(b.company)
    );

// --- Subscription -----------------------------------------------------------------

// "Today 17:05", "22 Sep 17:05", or "Final" once bidding has closed
const updatedLabel = (row, today) => {
  if (row.phase === 'closed') return 'Final';
  const stamp = toIstStamp(row.refreshedAt);
  if (!stamp) return '—';
  const [date, time] = stamp.split('T');
  return date === today ? `Today ${time}` : `${formatDayShort(date)} ${time}`;
};

/**
 * Rows with bidding figures for the board: live issues by demand first, then
 * closed ones, most recent first.
 *
 * @param {object[]} rows - mapped subscription rows (selectSubscriptionRows + selectSmeSubscriptionRows)
 */
export const buildDashboardSubscriptions = (rows, board, { limit = 6, today = todayIso() } = {}) => {
  const seen = new Set();
  return rows
    .filter((row) => row.board === board && row.hasData && typeof row.total === 'number')
    .filter((row) => {
      const key = row.symbol || row.company;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort(compareSubscriptionRows)
    .slice(0, limit)
    .map((row) => ({
      id: row.symbol || row.company,
      company: row.company,
      symbol: row.symbol,
      closed: row.phase === 'closed',
      qib: row.qib,
      nii: row.nii,
      retail: row.retail,
      total: row.total,
      updated: updatedLabel(row, today),
    }));
};

// --- Broker reviews -----------------------------------------------------------------

export const ratingBucket = (rating) => {
  const text = String(rating || '');
  if (/avoid|not subscribe|sell/i.test(text)) return 'avoid';
  if (/caution|may apply|risky|long[-\s]?term/i.test(text)) return 'mayApply';
  if (/subscribe|apply|buy/i.test(text)) return 'apply';
  return 'neutral';
};

const countCalls = (payload) => {
  const counts = { apply: 0, mayApply: 0, neutral: 0, avoid: 0 };
  const reviews = Array.isArray(payload?.reviews) ? payload.reviews : [];
  const seen = new Set();
  reviews.forEach((review) => {
    const broker = String(review?.broker_name || review?.broker || '').trim().toLowerCase();
    if (broker && seen.has(broker)) return;
    if (broker) seen.add(broker);
    counts[ratingBucket(review?.rating || review?.call)] += 1;
  });
  // No review list: fall back to the endpoint's own tally
  if (!reviews.length) {
    Object.entries(payload?.summary?.rating_counts || {}).forEach(([rating, count]) => {
      counts[ratingBucket(rating)] += Number(count) || 0;
    });
  }
  return counts;
};

const mergeReviewPayloads = (payloads) => ({
  reviews: payloads.flatMap((payload) => (Array.isArray(payload?.reviews) ? payload.reviews : [])),
  summary: { rating_counts: payloads.length === 1 ? payloads[0]?.summary?.rating_counts : {} },
});

/**
 * Analyst consensus per issue, only for issues with at least one call, most
 * reviewed first.
 *
 * @param {object[]} entries - [{ company, link, payloads: [broker-reviews data, ...] }]
 */
export const buildBrokerRatingRows = (entries) =>
  entries
    .map(({ company, link, payloads }) => {
      const counts = countCalls(payloads.length > 1 ? mergeReviewPayloads(payloads) : payloads[0]);
      const total = counts.apply + counts.mayApply + counts.neutral + counts.avoid;
      return { id: company, company, link: link || null, ...counts, total };
    })
    .filter((row) => row.total > 0)
    .sort((a, b) => b.total - a.total || b.apply - a.apply || a.company.localeCompare(b.company));

/**
 * Catalog ids for the board's issues, to request their broker reviews.
 * The catalog can list a company more than once, so each issue maps to every
 * matching id.
 *
 * @param {object[]} ipos - toDashboardIpos rows
 * @param {object[]} records - GET /api/core/ipos items
 */
export const catalogIdsFor = (ipos, records) =>
  ipos.map((ipo) => ({
    ipo,
    ids: records.filter((record) => record?.company_name && isSameCompany(record.company_name, ipo.company)).map((record) => String(record.id)),
  }));

// --- Documents -----------------------------------------------------------------------

const DOC_TONES = { DRHP: 'amber', RHP: 'rose', Prospectus: 'teal' };

/**
 * Latest offer documents for the board from the DRHP tracker
 * (GET /api/core/ipos/drhp-tracker, which tags each filing's board).
 *
 * @param {object[]} rows - utils/drhpTracker.js buildDrhpRows
 */
export const buildDashboardDocuments = (rows, board, limit = 3) =>
  rows
    .filter((row) => row.board === board)
    .sort(
      (a, b) =>
        (b.documentDate || '').localeCompare(a.documentDate || '') ||
        (b.addedAt || '').localeCompare(a.addedAt || '') ||
        a.company.localeCompare(b.company)
    )
    .slice(0, limit)
    .map((row) => ({
      id: row.id,
      type: row.docType,
      tone: DOC_TONES[row.docType],
      meta: [row.documentDate && `Filed ${formatShortDate(row.documentDate)}`, row.openOn && `Bidding ${formatShortDate(row.openOn)}`].find(Boolean) || '',
      title: row.company,
      note: [DRHP_STATUS[row.status].label, row.leadManager].filter(Boolean).join(' · '),
      action: row.analysisReady ? 'View DRHP Analysis' : 'View Filing',
      to: row.detailPath,
    }));

// --- Search ---------------------------------------------------------------------------------

/**
 * The dashboard's company search, applied to every card: IPO, subscription and
 * rating rows carry `company` (and a `symbol` where NSE lists the issue),
 * document cards carry the company as `title`.
 */
export const filterDashboardRows = (rows = [], query = '') => {
  const needle = String(query || '').trim().toLowerCase();
  if (!needle) return rows;
  return rows.filter((row) =>
    [row.company, row.title, row.symbol].some((value) => typeof value === 'string' && value.toLowerCase().includes(needle))
  );
};

// --- Stats ----------------------------------------------------------------------------------

export const computeDashboardStats = ({ ipos = [], subscriptions = [], ratings = [] }) => {
  const count = (status) => ipos.filter((ipo) => ipo.status === status).length;
  const [top] = [...subscriptions].sort((a, b) => b.total - a.total);
  return {
    total: ipos.length,
    pipeline: count('open') + count('upcoming'),
    open: count('open'),
    yetToList: count('yet_to_list'),
    listed: count('listed'),
    topSubscription: top ? { total: top.total, company: top.company } : null,
    reviews: ratings.reduce((sum, row) => sum + row.total, 0),
    reviewedIpos: ratings.length,
  };
};
